import { useMemo, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, ChevronDown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  certificatesForCompanyGroup,
  companyCatalogGroups,
  companyDrugOptions,
  companyServiceLabel,
  isCompanyDrugService,
  type CompanyLang,
} from "@/data/company";
import { companyPages } from "@/i18n/companyPages";
import { cn } from "@/lib/utils";

type SampleType = "ej-valt" | "saliv" | "urin";

type CompanyInquiryFormProps = {
  lang: CompanyLang;
  initialServices?: string[];
  initialSample?: SampleType;
};

export const CompanyInquiryForm = ({
  lang,
  initialServices = [],
  initialSample = "ej-valt",
}: CompanyInquiryFormProps) => {
  const copy = companyPages[lang].kontakt;
  const { toast } = useToast();
  const [selectedServices, setSelectedServices] = useState(initialServices);
  const [sampleType, setSampleType] = useState<SampleType>(initialSample);
  const [selectionError, setSelectionError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const dropdownRef = useRef<HTMLDetailsElement>(null);

  const hasDrugService = selectedServices.some(isCompanyDrugService);
  const chosenServices = selectedServices.filter((slug) => slug !== "flera-tjanster");

  const serviceGroups = useMemo(
    () => [
      {
        title: copy.drugGroup,
        options: companyDrugOptions.map((option) => ({
          slug: option.slug,
          title: option.title[lang],
        })),
      },
      ...companyCatalogGroups.map((group) => ({
        title: group.title[lang],
        options: certificatesForCompanyGroup(group.id).map((cert) => ({
          slug: cert.slug,
          title: cert.title[lang],
        })),
      })),
    ],
    [copy.drugGroup, lang],
  );

  const summaryLabel =
    chosenServices.length === 0
      ? copy.servicesPlaceholder
      : chosenServices.length === 1
        ? companyServiceLabel(chosenServices[0], lang)
        : copy.servicesSelectedMany.replace("{count}", String(chosenServices.length));

  const toggleService = (slug: string) => {
    setSelectedServices((previous) =>
      previous.includes(slug) ? previous.filter((value) => value !== slug) : [...previous, slug],
    );
    setSelectionError(false);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedServices.length) {
      setSelectionError(true);
      return;
    }

    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") || "").trim();
    const email = String(data.get("email") || "").trim();
    const company = String(data.get("company") || "").trim();
    const phone = String(data.get("phone") || "").trim();
    const people = String(data.get("people") || "").trim();
    const workplace = String(data.get("workplace") || "").trim();
    const period = String(data.get("period") || "").trim();
    const description = String(data.get("description") || "").trim();
    const consent = data.get("consent") === "on";
    const website = String(data.get("website") || "").trim();

    if (website) return;
    if (!name || !email || !company || !people || description.length < 10 || !consent) {
      toast({
        title: lang === "sv" ? "Kontrollera fälten" : "Please check the fields",
        variant: "destructive",
      });
      return;
    }

    const serviceLabels = selectedServices.map((slug) => companyServiceLabel(slug, lang)).join(", ");
    const sampleLabel =
      sampleType === "saliv" ? copy.sampleSaliv : sampleType === "urin" ? copy.sampleUrin : copy.sampleHelp;

    const details = [
      `${copy.servicesLegend}: ${serviceLabels}`,
      hasDrugService ? `${copy.sampleLabel}: ${sampleLabel}` : null,
      `${copy.peopleCount}: ${people}`,
      workplace ? `${copy.workplace}: ${workplace}` : null,
      period ? `${copy.period}: ${period}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    setSubmitting(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "company",
          name,
          email,
          company,
          phone,
          message: `${details}\n\n${description}`,
        }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(
          result?.error ||
            (lang === "sv" ? "Kunde inte skicka förfrågan." : "Could not send the inquiry."),
        );
      }
      setSent(true);
    } catch (error) {
      toast({
        title: lang === "sv" ? "Något gick fel" : "Something went wrong",
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-card sm:p-10" role="status">
        <CheckCircle2 className="h-8 w-8 text-primary" aria-hidden />
        <h2 className="mt-4 font-display text-2xl font-semibold text-foreground">{copy.successTitle}</h2>
        <p className="mt-3 text-muted-foreground">{copy.successText}</p>
        <ul className="mt-5 space-y-2 text-sm text-foreground">
          {selectedServices.map((slug) => (
            <li key={slug}>• {companyServiceLabel(slug, lang)}</li>
          ))}
        </ul>
        {hasDrugService ? (
          <p className="mt-4 text-sm text-muted-foreground">
            {copy.successSample}{" "}
            {sampleType === "saliv"
              ? copy.sampleSaliv
              : sampleType === "urin"
                ? copy.sampleUrin
                : copy.sampleHelp}
            .
          </p>
        ) : null}
        <p className="mt-4 text-sm text-muted-foreground">{copy.successNote}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-3xl border border-border/60 bg-card p-6 shadow-card sm:p-10"
    >
      <span className="text-sm font-semibold uppercase tracking-wider text-primary">{copy.formEyebrow}</span>
      <h2 className="mt-2 font-display text-2xl font-semibold text-foreground sm:text-3xl">{copy.formTitle}</h2>
      <p className="mt-2 text-muted-foreground">{copy.formSubtitle}</p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <fieldset className="sm:col-span-2">
          <legend className="text-sm font-medium text-foreground">{copy.servicesLegend}</legend>
          <details ref={dropdownRef} className="mt-2 rounded-2xl border border-border bg-background">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-foreground">
              <span>{summaryLabel}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </summary>
            <div className="max-h-80 space-y-4 overflow-y-auto border-t border-border px-4 py-4">
              {serviceGroups.map((group) => (
                <div key={group.title}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">{group.title}</p>
                  <div className="mt-2 space-y-2">
                    {group.options.map((option) => (
                      <label key={option.slug} className="flex items-start gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-input"
                          checked={selectedServices.includes(option.slug)}
                          onChange={() => toggleService(option.slug)}
                        />
                        <span>{option.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border px-4 py-3">
              <Button
                type="button"
                variant="ghost-outline"
                size="sm"
                onClick={() => {
                  if (dropdownRef.current) dropdownRef.current.open = false;
                }}
              >
                {copy.servicesDone}
              </Button>
            </div>
          </details>
          <label className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-input"
              checked={selectedServices.includes("flera-tjanster")}
              onChange={() => toggleService("flera-tjanster")}
            />
            <span>{copy.servicesHelp}</span>
          </label>
          {selectionError ? (
            <p className="mt-2 text-sm text-destructive" role="alert">
              {copy.servicesError}
            </p>
          ) : null}
        </fieldset>

        {selectedServices.includes("drogtest-misstanke") ? (
          <p className="sm:col-span-2 rounded-2xl border border-primary/20 bg-primary-soft/60 px-4 py-3 text-sm text-foreground">
            {copy.urgentNote}{" "}
            <a href="tel:+46317617700" className="font-medium text-primary underline-offset-2 hover:underline">
              {copy.urgentCall}
            </a>{" "}
            {copy.urgentRest}
          </p>
        ) : null}

        {hasDrugService ? (
          <div className="sm:col-span-2">
            <Label htmlFor="sampleType">{copy.sampleLabel}</Label>
            <select
              id="sampleType"
              name="sampleType"
              value={sampleType}
              onChange={(e) => setSampleType(e.target.value as SampleType)}
              className={cn(
                "mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              )}
            >
              <option value="ej-valt">{copy.sampleHelp}</option>
              <option value="saliv">{copy.sampleSaliv}</option>
              <option value="urin">{copy.sampleUrin}</option>
            </select>
          </div>
        ) : null}

        <div>
          <Label htmlFor="name">{copy.contactPerson} *</Label>
          <Input id="name" name="name" required maxLength={100} autoComplete="name" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="company">{copy.company} *</Label>
          <Input id="company" name="company" required maxLength={160} autoComplete="organization" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="email">{copy.email} *</Label>
          <Input id="email" name="email" type="email" required maxLength={254} autoComplete="email" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="phone">{copy.phoneOptional}</Label>
          <Input id="phone" name="phone" type="tel" maxLength={40} autoComplete="tel" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="people">{copy.peopleCount} *</Label>
          <Input id="people" name="people" type="number" min={1} max={10000} defaultValue={1} required className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="workplace">{copy.workplace}</Label>
          <Input
            id="workplace"
            name="workplace"
            maxLength={160}
            placeholder={copy.workplacePlaceholder}
            className="mt-1.5"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="period">{copy.period}</Label>
          <Input
            id="period"
            name="period"
            maxLength={160}
            placeholder={copy.periodPlaceholder}
            className="mt-1.5"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="description">{copy.description} *</Label>
          <Textarea
            id="description"
            name="description"
            required
            minLength={10}
            maxLength={1000}
            rows={5}
            placeholder={copy.descriptionPlaceholder}
            className="mt-1.5"
          />
        </div>
        <div className="hidden" aria-hidden="true">
          <Label htmlFor="website">Website</Label>
          <Input id="website" name="website" tabIndex={-1} autoComplete="off" />
        </div>
        <p className="sm:col-span-2 text-sm text-muted-foreground">{copy.privacy}</p>
        <label className="sm:col-span-2 flex items-start gap-3 text-sm text-muted-foreground">
          <input type="checkbox" name="consent" required className="mt-1 h-4 w-4 rounded border-input" />
          <span>{copy.consent}</span>
        </label>
        <div className="sm:col-span-2">
          <Button type="submit" variant="hero" size="xl" disabled={submitting} className="w-full">
            {submitting ? "…" : copy.submit}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">{copy.submitHint}</p>
        </div>
      </div>
    </form>
  );
};

export const CompanyNextSteps = ({ lang }: { lang: CompanyLang }) => {
  const copy = companyPages[lang].kontakt;
  return (
    <aside className="rounded-3xl border border-border/60 bg-card p-6 shadow-card sm:p-8">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <ShieldCheck className="h-6 w-6" />
      </div>
      <h2 className="mt-5 font-display text-xl font-semibold text-foreground">{copy.nextTitle}</h2>
      <ol className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
        {copy.nextSteps.map((step, index) => (
          <li key={step} className="flex gap-3">
            <span className="font-semibold text-primary">{String(index + 1).padStart(2, "0")}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <p className="mt-5 text-sm text-muted-foreground">{copy.nextNote}</p>
    </aside>
  );
};
